/**
 * iter249 — Franchise Setup panel
 *
 * Simplified per user (iter251):
 *   Franchise Setup = SELECTION ONLY
 *     1. Multi-select PO Masters + Invoice Masters
 *     2. Save Setup
 *     3. Optional Onboarding Cost Summary
 *   All Edit Items / Generate PO / Generate Invoice actions live on the
 *   Documents (PO / Invoice / Go-Live) tab.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { CheckSquare, Square, Search, ChevronDown, ChevronRight, Save, Plus, Trash2, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const currency = (v, code = 'INR') => {
  try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(v || 0); }
  catch { return `${code} ${(v || 0).toLocaleString()}`; }
};

async function api(path, body, token) {
  const res = await fetch(`${API}/api/masters${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...body }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof j.detail === 'string' ? j.detail : (j.detail?.message || 'Request failed'));
  return j;
}

/* ── Multi-select searchable checkbox picker ── */
function MasterMultiSelect({ label, options, value, onChange, testidPrefix }) {
  const [q, setQ] = useState('');
  // iter250 — dedupe options by worksheet_name so React never sees
  // duplicate keys (stale/legacy master docs share a worksheet name).
  const uniqOptions = useMemo(() => {
    const seen = new Set();
    return (options || []).filter(o => {
      const k = o?.worksheet_name;
      if (!k || seen.has(k)) return false;
      seen.add(k); return true;
    });
  }, [options]);
  const filtered = useMemo(() => uniqOptions.filter(o =>
    !q || o.worksheet_name.toLowerCase().includes(q.toLowerCase())), [uniqOptions, q]);
  const set = useMemo(() => new Set(value), [value]);
  const toggle = (ws) => onChange(value.includes(ws) ? value.filter(x => x !== ws) : [...value, ws]);
  const selectAll = () => onChange(Array.from(new Set([...value, ...filtered.map(o => o.worksheet_name)])));
  const clearAll = () => onChange([]);
  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 w-3 h-3 text-muted-foreground" />
          <Input className="pl-7 h-8 text-xs" placeholder={`Search ${label}…`} value={q} onChange={e => setQ(e.target.value)} data-testid={`${testidPrefix}-search`} />
        </div>
        <Button size="sm" variant="outline" onClick={selectAll} data-testid={`${testidPrefix}-select-all`}>Select all</Button>
        <Button size="sm" variant="ghost" onClick={clearAll} data-testid={`${testidPrefix}-clear-all`}>Clear</Button>
        <Badge variant="outline" className="text-[10px]">{value.length}/{uniqOptions.length}</Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-64 overflow-y-auto border rounded p-2 bg-muted/20">
        {filtered.map(o => (
          <label key={o.worksheet_name} className={`text-xs flex items-center gap-2 py-1 px-2 rounded cursor-pointer hover:bg-muted/40 ${set.has(o.worksheet_name) ? 'bg-primary/10 font-semibold' : ''}`} data-testid={`${testidPrefix}-opt-${o.worksheet_name.replace(/\s+/g, '-')}`}>
            {set.has(o.worksheet_name) ? <CheckSquare className="w-3 h-3 text-primary" /> : <Square className="w-3 h-3 text-muted-foreground" />}
            <input type="checkbox" className="hidden" checked={set.has(o.worksheet_name)} onChange={() => toggle(o.worksheet_name)} />
            <span className="flex-1 truncate">{o.worksheet_name}</span>
            <span className="text-[10px] text-muted-foreground">{o.row_count}</span>
          </label>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-2 text-xs">No matches</div>}
      </div>
    </div>
  );
}

/* ── Snapshot Editor (for a single franchise snapshot) ── */
export function SnapshotEditor({ snapshot, onSaved, onClose, token }) {
  const [items, setItems] = useState(snapshot.items || []);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const total = items.reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.rate || 0)), 0);
  const missing = items.filter(it => !it.rate).length;

  const toggleSel = (id) => setSelected(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === items.length ? new Set() : new Set(items.map(it => it.id)));

  const patchItem = async (item) => {
    try {
      await api(`/franchise/snapshot/${snapshot.id}/item/save`, { item }, token);
      const r = await api(`/franchise/snapshot/${snapshot.id}`, {}, token);
      setItems(r.items || []); onSaved && onSaved();
    } catch (e) { toast.error(e.message); }
  };

  const bulkDel = async () => {
    if (selected.size === 0) return toast.error('Select items');
    if (!window.confirm(`Delete ${selected.size} item(s)?`)) return;
    try {
      await api(`/franchise/snapshot/${snapshot.id}/items/delete`, { item_ids: Array.from(selected) }, token);
      const r = await api(`/franchise/snapshot/${snapshot.id}`, {}, token);
      setItems(r.items || []); setSelected(new Set()); onSaved && onSaved();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-3" data-testid={`snap-editor-${snapshot.id}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm">
          Edit — {snapshot.worksheet_name} <Badge variant="outline" className="text-[9px]">{snapshot.master_type}</Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setEditing({ id: '', sr: items.length + 1, item: '', specification: '', side: '', unit: 'NO.', quantity: 1, rate: null, gst_percent: 0, remarks: '' })} data-testid="btn-snap-add"><Plus className="w-3 h-3 mr-1" />Add</Button>
          {selected.size > 0 && <Button size="sm" variant="destructive" onClick={bulkDel} data-testid="btn-snap-bulk-del"><Trash2 className="w-3 h-3 mr-1" />Delete ({selected.size})</Button>}
          <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        {items.length} items · Estimated {currency(total)}{missing > 0 && ` · ${missing} without rate`}
      </div>
      <div className="border rounded overflow-x-auto max-h-[60vh]">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 sticky top-0">
            <tr className="text-left border-b">
              <th className="p-2 w-6"><input type="checkbox" checked={items.length > 0 && selected.size === items.length} onChange={toggleAll} /></th>
              <th className="p-2 w-8">Sr</th>
              <th className="p-2">Item</th>
              <th className="p-2 w-16">U/M</th>
              <th className="p-2 w-16 text-right">Qty</th>
              <th className="p-2 w-24 text-right">Rate</th>
              <th className="p-2 w-14 text-center">GST%</th>
              <th className="p-2 w-24 text-right">Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => {
              const amt = (Number(it.quantity || 0) * Number(it.rate || 0));
              return (
                <tr key={it.id} className={`border-b ${!it.rate ? 'bg-amber-50' : ''}`} data-testid={`snap-item-${it.id}`}>
                  <td className="p-2"><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSel(it.id)} /></td>
                  <td className="p-2 text-muted-foreground">{it.sr}</td>
                  <td className="p-2">
                    <div className="font-semibold">{it.item || <em className="text-muted-foreground">—</em>}</div>
                    {it.specification && <div className="text-[10px] text-muted-foreground whitespace-pre-line">{it.specification}</div>}
                  </td>
                  <td className="p-2">{it.unit || '—'}</td>
                  <td className="p-2 text-right">{it.quantity ?? '—'}</td>
                  <td className={`p-2 text-right ${!it.rate ? 'text-amber-700 font-semibold' : ''}`}>{it.rate ? currency(it.rate) : 'RATE REQ'}</td>
                  <td className="p-2 text-center">{it.gst_percent ?? 0}</td>
                  <td className="p-2 text-right font-semibold">{amt > 0 ? currency(amt) : '—'}</td>
                  <td className="p-2"><Button size="sm" variant="ghost" onClick={() => setEditing({ ...it })} data-testid={`btn-snap-edit-${it.id}`}><Save className="w-3 h-3" /></Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent data-testid="snap-item-modal">
          <DialogHeader><DialogTitle>{editing?.id ? 'Edit' : 'Add'} Item</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="col-span-2"><Label>Item *</Label><Input value={editing.item || ''} onChange={e => setEditing(f => ({ ...f, item: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Specification</Label><Textarea rows={2} value={editing.specification || ''} onChange={e => setEditing(f => ({ ...f, specification: e.target.value }))} /></div>
              <div><Label>U/M</Label><Input value={editing.unit || ''} onChange={e => setEditing(f => ({ ...f, unit: e.target.value }))} /></div>
              <div><Label>Qty</Label><Input type="number" value={editing.quantity ?? ''} onChange={e => setEditing(f => ({ ...f, quantity: e.target.value === '' ? null : +e.target.value }))} /></div>
              <div><Label>Rate</Label><Input type="number" value={editing.rate ?? ''} onChange={e => setEditing(f => ({ ...f, rate: e.target.value === '' ? null : +e.target.value }))} /></div>
              <div><Label>GST %</Label><Input type="number" value={editing.gst_percent ?? 0} onChange={e => setEditing(f => ({ ...f, gst_percent: +e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button><Button onClick={async () => { await patchItem(editing); setEditing(null); }} data-testid="btn-snap-save-item">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Generate PO wizard ── */
function GeneratePOWizard({ snapshot, onboarding, token, onClose, onGenerated }) {
  const [form, setForm] = useState({
    vendor: '', vendor_gstin: '', vendor_address: '', vendor_contact: '',
    expected_delivery: '', gst_split: 'CGST_SGST',
    terms: 'Payment 30 days from invoice. Delivery against a signed GRN.',
    remarks: '',
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const cur = onboarding?.currency_code || 'INR';
  const items = snapshot?.items || [];
  const total = items.reduce((s, it) => s + (Number(it.quantity || 0) * Number(it.rate || 0)), 0);
  const missing = items.filter(it => !it.rate).length;

  const submit = async () => {
    if (!form.vendor.trim()) return toast.error('Vendor required');
    if (missing > 0) return toast.error(`${missing} item(s) missing rate — edit configuration first`);
    setBusy(true);
    try {
      const r = await api('/franchise/generate-po', {
        onboarding_id: onboarding.onboarding_id, snapshot_id: snapshot.id,
        ...form, currency_code: cur,
      }, token);
      setResult(r); toast.success(`PO ${r.po_number} created`);
      onGenerated && onGenerated();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    if (!result) return;
    try {
      const res = await fetch(`${API}/api/franchise-onboarding/po/pdf/${result.po_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error('PDF failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `PO_${result.po_number.replace(/\//g, '-')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={!!snapshot} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto" data-testid="gen-po-wizard">
        <DialogHeader><DialogTitle>Create PO — {snapshot?.worksheet_name}</DialogTitle></DialogHeader>
        {!result ? (
          <>
            <div className="border border-blue-300 bg-blue-50 rounded p-3 text-xs">
              <div className="font-semibold text-blue-800 mb-1">📌 Pre-loaded from franchise snapshot</div>
              <div>{items.length} items · Estimated {currency(total, cur)}{missing > 0 && ` · ⚠ ${missing} without rate`}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Label>Vendor *</Label><Input value={form.vendor} onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} data-testid="gen-po-vendor" /></div>
              <div><Label>Vendor GSTIN</Label><Input value={form.vendor_gstin} onChange={e => setForm(f => ({ ...f, vendor_gstin: e.target.value }))} /></div>
              <div className="col-span-2"><Label>Vendor Address</Label><Input value={form.vendor_address} onChange={e => setForm(f => ({ ...f, vendor_address: e.target.value }))} /></div>
              <div><Label>Vendor Contact</Label><Input value={form.vendor_contact} onChange={e => setForm(f => ({ ...f, vendor_contact: e.target.value }))} /></div>
              <div><Label>Expected Delivery</Label><Input type="date" value={form.expected_delivery} onChange={e => setForm(f => ({ ...f, expected_delivery: e.target.value }))} /></div>
              <div><Label>GST Split</Label><Input value={form.gst_split} onChange={e => setForm(f => ({ ...f, gst_split: e.target.value }))} /></div>
              <div><Label>Currency</Label><Input value={cur} disabled /></div>
              <div className="col-span-3"><Label>Terms</Label><Textarea rows={2} value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} /></div>
              <div className="col-span-3"><Label>Remarks</Label><Input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} /></div>
            </div>
            <div className="border rounded overflow-x-auto max-h-64">
              <table className="w-full text-xs"><thead className="bg-muted/40 sticky top-0"><tr className="text-left border-b"><th className="p-2 w-8">Sr</th><th className="p-2">Item</th><th className="p-2 w-16">U/M</th><th className="p-2 w-16 text-right">Qty</th><th className="p-2 w-24 text-right">Rate</th><th className="p-2 w-24 text-right">Amount</th></tr></thead>
                <tbody>{items.map(it => { const amt = Number(it.quantity || 0) * Number(it.rate || 0); return (
                  <tr key={it.id} className={`border-b ${!it.rate ? 'bg-amber-50' : ''}`}><td className="p-2 text-muted-foreground">{it.sr}</td><td className="p-2"><div className="font-semibold">{it.item}</div>{it.specification && <div className="text-[10px] text-muted-foreground whitespace-pre-line">{it.specification}</div>}</td><td className="p-2">{it.unit || '—'}</td><td className="p-2 text-right">{it.quantity ?? '—'}</td><td className="p-2 text-right">{it.rate ? currency(it.rate, cur) : 'RATE REQ'}</td><td className="p-2 text-right font-semibold">{amt > 0 ? currency(amt, cur) : '—'}</td></tr>
                ); })}</tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="border rounded p-3 bg-green-50 border-green-300">
              <div className="text-sm font-semibold text-green-800">PO created — {result.po_number}</div>
            </div>
            <Button onClick={downloadPDF} data-testid="btn-download-po-pdf"><Download className="w-4 h-4 mr-1" />Download PDF</Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && <Button onClick={submit} disabled={busy || missing > 0} data-testid="btn-gen-po-submit">{busy ? 'Creating…' : 'Generate PO'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Generate Invoice wizard ── */
export function GenerateInvoiceWizard({ snapshot, onboarding, token, onClose, onGenerated }) {
  const [form, setForm] = useState({ due_date: '', remarks: '', invoice_issued_by: 'MFPL' });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const cur = onboarding?.currency_code || 'INR';
  // iter254 — Local editable copy of snapshot items.  Edits stay
  // franchise-scoped: the underlying master snapshot is never
  // mutated by this wizard.
  const [lines, setLines] = useState([]);
  const [selected, setSelected] = useState(new Set());
  useEffect(() => {
    const base = (snapshot?.items || []).map((it, idx) => ({
      _rowId: it.id || `SNAP-${idx}`,
      item: it.item || it.description || '',
      specification: it.specification || '',
      side: it.side || '',
      unit: it.unit || 'NO',
      quantity: Number(it.quantity || 0),
      rate: it.rate === null || it.rate === '' ? null : Number(it.rate || 0),
      gst_percent: Number(it.gst_percent || 0),
      remarks: '',
    }));
    setLines(base);
    setSelected(new Set());
  }, [snapshot?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const patch = (idx, patch) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addLine = () => setLines(ls => [...ls, {
    _rowId: `NEW-${Date.now()}`,
    item: '', specification: '', side: '',
    unit: 'NO', quantity: 1, rate: null, gst_percent: 0, remarks: '',
  }]);
  const delLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));
  const toggleSel = (rowId) => setSelected(s => { const n = new Set(s); n.has(rowId) ? n.delete(rowId) : n.add(rowId); return n; });
  const toggleSelAll = () => setSelected(s => s.size === lines.length ? new Set() : new Set(lines.map(l => l._rowId)));
  const bulkDel = () => { setLines(ls => ls.filter(l => !selected.has(l._rowId))); setSelected(new Set()); };

  // Live totals
  const totals = lines.reduce((acc, l) => {
    const qty = Number(l.quantity || 0);
    const rate = Number(l.rate || 0);
    const amt = qty * rate;
    const gst = amt * (Number(l.gst_percent || 0) / 100);
    acc.subtotal += amt; acc.gst += gst; acc.grand += amt + gst;
    return acc;
  }, { subtotal: 0, gst: 0, grand: 0 });
  const missingRate = lines.filter(l => l.rate === null || l.rate === '' || Number.isNaN(Number(l.rate))).length;
  const missingItem = lines.filter(l => !String(l.item || '').trim()).length;

  const submit = async () => {
    if (lines.length === 0) return toast.error('Add at least one line');
    if (missingItem > 0) return toast.error(`${missingItem} row(s) missing item name`);
    if (missingRate > 0) return toast.error(`${missingRate} row(s) with RATE REQUIRED — enter rate first`);
    setBusy(true);
    try {
      const r = await api('/franchise/generate-invoice', {
        onboarding_id: onboarding.onboarding_id,
        snapshot_id: snapshot.id,
        override_items: lines.map(l => ({
          item: l.item, specification: l.specification, side: l.side,
          unit: l.unit, quantity: Number(l.quantity || 0),
          rate: Number(l.rate || 0),
          gst_percent: Number(l.gst_percent || 0),
          remarks: l.remarks || '',
        })),
        ...form,
      }, token);
      setResult(r); toast.success(`Invoice ${r.invoice_number} created`);
      onGenerated && onGenerated();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const downloadPDF = async () => {
    if (!result) return;
    try {
      const res = await fetch(`${API}/api/franchise-onboarding/invoice/pdf/${result.invoice_id}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!res.ok) throw new Error('PDF failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `INV_${result.invoice_number.replace(/\//g, '-')}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={!!snapshot} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto" data-testid="gen-inv-wizard">
        <DialogHeader><DialogTitle>Generate Invoice — {snapshot?.worksheet_name}</DialogTitle></DialogHeader>
        {!result ? (
          <>
            <div className="border border-blue-300 bg-blue-50 rounded p-3 text-xs">
              <div className="font-semibold text-blue-800 mb-1">📌 Pre-loaded from franchise snapshot — items are editable below</div>
              <div>{lines.length} items · Estimated {currency(totals.grand, cur)}{missingRate > 0 && ` · ⚠ ${missingRate} without rate`}</div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Issued By</Label><Input value={form.invoice_issued_by} onChange={e => setForm(f => ({ ...f, invoice_issued_by: e.target.value }))} data-testid="inv-issued-by" /></div>
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="inv-due-date" /></div>
              <div><Label>Remarks</Label><Input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} data-testid="inv-remarks" /></div>
            </div>

            <div className="flex items-center gap-2 flex-wrap pt-1">
              <Button size="sm" variant="outline" onClick={addLine} data-testid="inv-add-line"><Plus className="w-3 h-3 mr-1" />Add Item</Button>
              <Button size="sm" variant="outline" onClick={bulkDel} disabled={selected.size === 0} data-testid="inv-bulk-del"><Trash2 className="w-3 h-3 mr-1" />Delete Selected ({selected.size})</Button>
              <div className="text-[11px] text-muted-foreground ml-auto">Subtotal: <b>{currency(totals.subtotal, cur)}</b> · GST: <b>{currency(totals.gst, cur)}</b> · Grand: <b className="text-primary">{currency(totals.grand, cur)}</b></div>
            </div>

            <div className="border rounded overflow-x-auto max-h-[420px]">
              <table className="w-full text-xs" data-testid="inv-edit-table">
                <thead className="bg-muted/40 sticky top-0 z-10">
                  <tr className="text-left border-b">
                    <th className="p-1 w-6"><input type="checkbox" checked={lines.length > 0 && selected.size === lines.length} onChange={toggleSelAll} data-testid="inv-sel-all" /></th>
                    <th className="p-1 w-8">Sr</th>
                    <th className="p-2">Item & Specification</th>
                    <th className="p-2 w-16">U/M</th>
                    <th className="p-2 w-16 text-right">Qty</th>
                    <th className="p-2 w-24 text-right">Rate</th>
                    <th className="p-2 w-14 text-center">GST%</th>
                    <th className="p-2 w-24 text-right">Amount</th>
                    <th className="p-2 w-40">Remarks</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const amt = Number(l.quantity || 0) * Number(l.rate || 0);
                    const noRate = l.rate === null || l.rate === '' || Number.isNaN(Number(l.rate));
                    return (
                      <tr key={l._rowId} className={`border-b ${noRate ? 'bg-amber-50' : ''}`} data-testid={`inv-row-${idx}`}>
                        <td className="p-1"><input type="checkbox" checked={selected.has(l._rowId)} onChange={() => toggleSel(l._rowId)} data-testid={`inv-sel-${idx}`} /></td>
                        <td className="p-1 text-muted-foreground">{idx + 1}</td>
                        <td className="p-1">
                          <Input value={l.item} onChange={e => patch(idx, { item: e.target.value })} className="h-7 text-xs" data-testid={`inv-item-${idx}`} />
                          <Input value={l.specification} onChange={e => patch(idx, { specification: e.target.value })} className="h-6 text-[10px] mt-1" placeholder="Specification (optional)" data-testid={`inv-spec-${idx}`} />
                        </td>
                        <td className="p-1"><Input value={l.unit} onChange={e => patch(idx, { unit: e.target.value })} className="h-7 text-xs" data-testid={`inv-unit-${idx}`} /></td>
                        <td className="p-1"><Input type="number" min="0" step="any" value={l.quantity} onChange={e => patch(idx, { quantity: e.target.value })} className="h-7 text-xs text-right" data-testid={`inv-qty-${idx}`} /></td>
                        <td className="p-1"><Input type="number" min="0" step="any" value={l.rate ?? ''} onChange={e => patch(idx, { rate: e.target.value === '' ? null : e.target.value })} placeholder={noRate ? 'RATE REQ' : ''} className={`h-7 text-xs text-right ${noRate ? 'border-amber-500' : ''}`} data-testid={`inv-rate-${idx}`} /></td>
                        <td className="p-1"><Input type="number" min="0" step="any" value={l.gst_percent} onChange={e => patch(idx, { gst_percent: e.target.value })} className="h-7 text-xs text-center" data-testid={`inv-gst-${idx}`} /></td>
                        <td className="p-2 text-right font-semibold" data-testid={`inv-amt-${idx}`}>{amt > 0 ? currency(amt, cur) : '—'}</td>
                        <td className="p-1"><Input value={l.remarks} onChange={e => patch(idx, { remarks: e.target.value })} className="h-7 text-xs" data-testid={`inv-remarks-${idx}`} /></td>
                        <td className="p-1 text-right"><Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => delLine(idx)} data-testid={`inv-del-${idx}`}><Trash2 className="w-3 h-3 text-red-600" /></Button></td>
                      </tr>
                    );
                  })}
                  {lines.length === 0 && (
                    <tr><td colSpan={10} className="p-4 text-center text-muted-foreground text-xs">No lines. Click "Add Item" to start.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <div className="border rounded p-3 bg-green-50 border-green-300">
              <div className="text-sm font-semibold text-green-800">Invoice created — {result.invoice_number}</div>
            </div>
            <Button onClick={downloadPDF} data-testid="btn-download-inv-pdf"><Download className="w-4 h-4 mr-1" />Download PDF</Button>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && <Button onClick={submit} disabled={busy || missingRate > 0 || lines.length === 0} data-testid="btn-gen-inv-submit">{busy ? 'Creating…' : 'Generate Invoice'}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Main Franchise Setup tab ── */
export function FranchiseSetupTab({ token, oid, ob, isSA, onReload }) {
  const [masters, setMasters] = useState({ po_masters: [], invoice_masters: [] });
  const [snapshotView, setSnapshotView] = useState({ po_snapshots: [], invoice_snapshots: [], selected_po_masters: [], selected_invoice_masters: [], cost_summary: { po_total: 0, invoice_total: 0, grand_total: 0 } });
  const [selPo, setSelPo] = useState([]);
  const [selInv, setSelInv] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [openSection, setOpenSection] = useState({ setup: true, cost: true });

  const loadAll = useCallback(async () => {
    try {
      const [m, sv] = await Promise.all([
        api('/list', {}, token),
        api(`/franchise/list/${oid}`, {}, token),
      ]);
      setMasters({ po_masters: m.po_masters || [], invoice_masters: m.invoice_masters || [] });
      setSnapshotView(sv);
      setSelPo(sv.selected_po_masters || []);
      setSelInv(sv.selected_invoice_masters || []);
      setDirty(false);
    } catch (e) { toast.error(e.message); }
  }, [oid, token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSelection = async () => {
    try {
      await api('/franchise/select', {
        onboarding_id: oid,
        po_worksheets: selPo, invoice_worksheets: selInv,
      }, token);
      toast.success('Franchise setup saved');
      await loadAll(); onReload && onReload();
    } catch (e) { toast.error(e.message); }
  };

  const cur = ob?.currency_code || 'INR';

  return (
    <div className="space-y-4" data-testid="franchise-setup-tab">
      {/* ── Section 1: SELECT MASTERS ── */}
      <Collapsible open={openSection.setup} onOpenChange={v => setOpenSection(s => ({ ...s, setup: v }))}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
              <CardTitle className="text-base flex items-center gap-2">
                {openSection.setup ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Franchise Setup — Documents Required
              </CardTitle>
              {dirty && <Badge variant="outline" className="text-amber-700 border-amber-500 text-[10px]">Unsaved</Badge>}
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              <div>
                <div className="text-xs font-semibold mb-1">A. Purchase Orders Required</div>
                <MasterMultiSelect label="PO Master" options={masters.po_masters}
                  value={selPo} onChange={v => { setSelPo(v); setDirty(true); }} testidPrefix="pick-po" />
              </div>
              <div>
                <div className="text-xs font-semibold mb-1">B. Invoices Required</div>
                <MasterMultiSelect label="Invoice Master" options={masters.invoice_masters}
                  value={selInv} onChange={v => { setSelInv(v); setDirty(true); }} testidPrefix="pick-inv" />
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" onClick={saveSelection} disabled={!isSA && !dirty} data-testid="btn-save-setup">Save & Create Franchise Setup</Button>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* ── Section 2: COST SUMMARY ── */}
      <Collapsible open={openSection.cost} onOpenChange={v => setOpenSection(s => ({ ...s, cost: v }))}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="flex flex-row items-center justify-between cursor-pointer">
              <CardTitle className="text-base flex items-center gap-2">
                {openSection.cost ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Onboarding Cost Summary
              </CardTitle>
              <span className="text-xs text-muted-foreground">Auto-computed from selected masters</span>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <table className="w-full text-xs" data-testid="cost-summary-table">
                <tbody>
                  {snapshotView.po_snapshots.map(s => (
                    <tr key={s.id} className="border-b"><td className="p-2 font-semibold">{s.worksheet_name}</td><td className="p-2 text-right">{currency(s.configured_value, cur)}</td></tr>
                  ))}
                  {snapshotView.invoice_snapshots.map(s => (
                    <tr key={s.id} className="border-b"><td className="p-2 font-semibold">{s.worksheet_name}</td><td className="p-2 text-right">{currency(s.configured_value, cur)}</td></tr>
                  ))}
                  <tr className="bg-primary/10 font-bold"><td className="p-2">TOTAL ESTIMATED SETUP</td><td className="p-2 text-right" data-testid="cost-grand-total">{currency(snapshotView.cost_summary.grand_total, cur)}</td></tr>
                </tbody>
              </table>
              {snapshotView.po_snapshots.length === 0 && snapshotView.invoice_snapshots.length === 0 && (
                <div className="text-center py-4 text-muted-foreground text-xs">No masters selected yet — pick them above and hit Save.</div>
              )}
              <div className="mt-3 text-[11px] text-muted-foreground italic">
                To generate POs / Invoices or edit items, switch to the <b>Documents (PO/Invoice/Go-Live)</b> tab.
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}
